<template>
  <div class="availability-day-of-week-picker" role="group" :aria-label="label">
    <sp-checkbox
      v-for="item in dayItems"
      :key="item.value"
      :disabled="disabled"
      class="availability-day-of-week-picker__day"
      :label="item.label"
      :value="item.value"
      :checked="item.checked"
      :data-testid="TestId.finder.availabilityDay"
      @update-checked="handleUpdateChecked"
    ></sp-checkbox>
  </div>
</template>

<script setup>
import { toBoolean } from "#ce/utils/props";
import { computed } from "vue";
import { firstDetail } from "#ce/utils/events";
import { TestId } from "#/test-ids";
import { t } from "#ce/shared/filters/availability/i18n";

// Monday first, Sunday last, in a two-column grid: weekdays down the first column, the weekend
// down the second.
const ORDERED_DAYS = Object.freeze([1, 2, 3, 4, 5, 6, 0]);

const emit = defineEmits(["toggle-day"]);

const props = defineProps({
  disabled: { type: Boolean, default: false },
  /** The selected weekday numbers, Sunday `0`. */
  days: {
    type: Array,
    default: () => [],
  },
  label: {
    type: String,
    required: true,
  },
});

const dayItems = computed(() =>
  ORDERED_DAYS.map((day) => ({
    value: String(day),
    label: t(`weekday_long_${day}`),
    checked: props.days.includes(day),
  })),
);

// `value` is a declared `sp-checkbox` prop, so the `:value` binding writes the host property (the
// element reflects it to the attribute) and the handler reads that property back.
function handleUpdateChecked(event) {
  if (toBoolean(props.disabled)) {
    return;
  }
  emit("toggle-day", Number.parseInt(event.currentTarget.value), Boolean(firstDetail(event)));
}
</script>

<style lang="scss" scoped>
.availability-day-of-week-picker {
  // The day labels read in the body typescale. The checkbox sizes its label box from one token and
  // the label's sp-text takes its glyphs from another, so both carry the same font.
  --day-font: var(--sp-comp-availability-filter-day-font, var(--sp-sys-typescale-body-short));
  --sp-comp-checkbox-label-font: var(--day-font);
  --sp-comp-text-label-short: var(--day-font);

  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(5, min-content);
  gap: var(--sp-comp-availability-filter-day-gap, var(--sp-ref-scale-10)) var(--sp-ref-scale-6);

  // Monday to Friday fill the first column, Saturday and Sunday the second.
  @for $i from 1 through 5 {
    &__day:nth-child(#{$i}) {
      grid-area: #{$i} / 1;
    }
  }

  @for $i from 6 through 7 {
    &__day:nth-child(#{$i}) {
      grid-area: #{$i - 5} / 2;
    }
  }
}
</style>
